import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { GenerateReportButton } from '../../components/GenerateReportButton';
import { ReportDetailModal } from '../RiskPlusReport/RiskReportModal';
import type { RiskPlusReport } from '../../entities/report/RiskPlusReport';
import type { RiskPlusPageData } from './useRiskPlusPageData';
import type { TRiskRank, TRiskRankWithNA } from '../../entities/risk/Risk';

// eslint-disable-next-line react-refresh/only-export-components
export function riskColor(rank: TRiskRank | TRiskRankWithNA): string {
  switch (rank) {
    case 'critical': return 'var(--critical)';
    case 'high': return 'var(--high)';
    case 'moderate': return 'var(--moderate)';
    case 'low': return 'var(--low)';
    case 'negligible': return 'var(--risk-negligible)';
    default: return 'var(--risk-na)';
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export function riskLabel(rank: TRiskRank | TRiskRankWithNA): string {
  if (!rank || rank === 'n/a') return 'N/A';
  return rank.charAt(0).toUpperCase() + rank.slice(1);
}

// eslint-disable-next-line react-refresh/only-export-components
export function riskBgClass(rank: TRiskRank | TRiskRankWithNA): string {
  switch (rank) {
    case 'critical': return 'bg-red-100 text-aiquris-risk-critical';
    case 'high': return 'bg-yellow-100 text-aiquris-risk-high';
    case 'moderate': return 'bg-yellow-100 text-aiquris-risk-moderate';
    case 'low': return 'bg-aiquris-green-100 text-aiquris-risk-low';
    case 'negligible': return 'bg-aiquris-green-100 text-aiquris-risk-negligible';
    default: return 'bg-soft-gray text-gray-500';
  }
}

export function RiskBadge({ rank }: { rank: TRiskRank | TRiskRankWithNA }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-[4px] px-2 py-0.5 text-xxs font-semibold"
      style={{ background: riskColor(rank), color: '#fff' }}
    >
      {riskLabel(rank)}
    </span>
  );
}

export function SectionCard({
  title,
  children,
  className = '',
}: {
  title?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-xs ${className}`}>
      {title && (
        <div className="mb-3 text-sm font-semibold text-assured-dark-blue">
          {title}
        </div>
      )}
      {children}
    </div>
  );
}

export function StatTile({
  label,
  value,
  subLabel,
  dotColor,
  variant = 'default',
  dimmed = false,
  valueSize = '2xl',
}: {
  label: string;
  value: React.ReactNode;
  subLabel?: string;
  dotColor?: string;
  variant?: 'primary' | 'default';
  dimmed?: boolean;
  valueSize?: 'lg' | '2xl';
}) {
  const isPrimary = variant === 'primary';
  return (
    <div
      className={[
        'rounded-xl px-4 py-3 shadow-xs',
        isPrimary
          ? 'border border-light-sky-blue bg-slate-gray'
          : 'border border-gray-200 bg-white',
        !isPrimary && dimmed ? 'opacity-50' : '',
      ].join(' ')}
    >
      <div
        className={`flex items-center gap-1.5 text-xxxs font-semibold uppercase tracking-wider ${isPrimary ? 'text-deep-blue' : 'text-gray-400'
          }`}
      >
        {dotColor && (
          <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: dotColor }} />
        )}
        <span className="truncate">{label}</span>
      </div>
      <div
        className={`mt-2 font-semibold leading-none ${valueSize === 'lg' ? 'text-lg' : 'text-2xl'} ${isPrimary ? 'text-midnight-navi' : 'text-assured-dark-blue'
          }`}
      >
        {value}
      </div>
      {subLabel && (
        <div
          className={`mt-1 text-xxs truncate ${isPrimary ? 'text-deep-blue opacity-75' : 'text-gray-400'
            }`}
        >
          {subLabel}
        </div>
      )}
    </div>
  );
}

export function StatGrid({
  children,
  cols,
  className = '',
}: {
  children: React.ReactNode;
  cols?: number;
  className?: string;
}) {
  return (
    <div
      className={`grid gap-3 ${className}`}
      style={cols !== undefined ? { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` } : undefined}
    >
      {children}
    </div>
  );
}

export function BulletList({
  items,
  dotClass = 'bg-gray-300',
}: {
  items: string[];
  dotClass?: string;
}) {
  if (items.length === 0) return null;
  return (
    <ul className="space-y-1">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-xs text-midnight-navi">
          <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${dotClass}`} />
          {item}
        </li>
      ))}
    </ul>
  );
}

export function NumberedItem({
  index,
  title,
  description,
  children,
}: {
  index: number;
  title: string;
  description?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-soft-gray text-xxs font-semibold text-deep-blue">
        {index + 1}
      </span>
      <div className="min-w-0">
        <div className="text-xs font-semibold text-midnight-navi leading-snug">{title}</div>
        {description && description !== title && (
          <p className="mt-0.5 text-xs leading-relaxed text-gray-600">{description}</p>
        )}
        {children}
      </div>
    </div>
  );
}

export function LabelDescCard({
  label,
  description,
  index,
}: {
  label: string;
  description?: string;
  index?: number;
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-soft-gray px-4 py-3">
      {index !== undefined && (
        <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white border border-gray-200 text-xxs font-bold text-deep-blue">
          {index + 1}
        </div>
      )}
      <div>
        <div className="text-xs font-semibold text-midnight-navi">{label}</div>
        {description && description !== label && (
          <p className="mt-0.5 text-xs leading-relaxed text-gray-600">{description}</p>
        )}
      </div>
    </div>
  );
}

function NoReportEmptyState({
  resolvedChat,
  brief,
  refreshReports,
}: Pick<RiskPlusPageData, 'resolvedChat' | 'brief' | 'refreshReports'>) {
  return (
    <div className="mt-6 rounded-xl border border-dashed border-gray-300 bg-white px-6 py-10 text-center shadow-xs">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
        <FileText className="h-5 w-5 text-gray-400" />
      </div>
      <p className="text-sm font-medium text-midnight-navi">No Risk+ report generated yet</p>
      <p className="mt-1 text-xs text-gray-500">
        Generate a Risk+ report to see full analysis for this use case.
      </p>
      {resolvedChat && brief && (
        <div className="mt-4 flex justify-center">
          <GenerateReportButton
            useCaseChat={resolvedChat}
            reportType="risk"
            label="Generate Risk+ Report"
            disabled={brief.jobIsActive}
            onGenerateReport={refreshReports}
          />
        </div>
      )}
    </div>
  );
}

export function RiskPlusTopNav({
  useCaseChatId,
  secondaryNav,
}: {
  useCaseChatId: string;
  secondaryNav?: React.ReactNode;
}) {
  const navigate = useNavigate();

  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <Link
        to="/dashboard"
        className="flex items-center gap-2 text-sm font-medium text-royal-blue hover:text-deep-blue"
        style={{ lineHeight: 'normal' }}
      >
        <span style={{ lineHeight: '0.5' }}>←</span>
        <span className="hover:underline">Use Case Register</span>
      </Link>
      {secondaryNav ?? (
        <button
          type="button"
          className="text-sm font-medium text-royal-blue hover:text-deep-blue hover:underline"
          onClick={() =>
            navigate(`/chat/${encodeURIComponent(useCaseChatId)}/edit`, {
              state: { from: window.location.pathname },
            })
          }
        >
          Resume chat →
        </button>
      )}
    </div>
  );
}

export function RiskPlusPagerLink({
  to,
  label,
  direction,
}: {
  to: string;
  label: string;
  direction: 'prev' | 'next';
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-2 text-sm font-medium text-royal-blue hover:text-deep-blue"
      style={{ lineHeight: 'normal' }}
    >
      {direction === 'prev' && <span style={{ lineHeight: '0.5' }}>←</span>}
      <span className="hover:underline">{label}</span>
      {direction === 'next' && <span style={{ lineHeight: '0.5' }}>→</span>}
    </Link>
  );
}

export function RiskPlusPagerNav({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center" style={{ gap: 22 }}>
      {children}
    </div>
  );
}

interface RiskPlusPageShellProps {
  useCaseChatId: string;
  data: RiskPlusPageData;
  children: (report: RiskPlusReport) => React.ReactNode;
  secondaryNav?: React.ReactNode;
}

export function RiskPlusPageShell({ useCaseChatId, data, children, secondaryNav }: RiskPlusPageShellProps) {
  const navigate = useNavigate();
  const [showRiskModal, setShowRiskModal] = useState(false);

  const {
    brief,
    riskReport,
    resolvedChat,
    isLoadingBriefs,
    isLoadingReport,
    reportError,
    showReportsPanel,
    loadRiskReport,
    refreshReports,
  } = data;

  if (!useCaseChatId) return null;

  if (isLoadingBriefs) {
    return (
      <div className="min-h-screen bg-impact-plus-page p-6">
        <div className="mx-auto max-w-7xl animate-pulse space-y-3">
          <div className="h-4 w-40 rounded bg-gray-200" />
          <div className="h-16 rounded-xl bg-white" />
          <div className="h-32 rounded-xl bg-gray-100" />
        </div>
      </div>
    );
  }

  if (!brief) {
    return (
      <div className="min-h-screen bg-impact-plus-page p-6">
        <div className="mx-auto max-w-7xl rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <p className="text-midnight-navi">Use case not found.</p>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="mt-4 text-sm font-medium text-royal-blue hover:text-deep-blue hover:underline"
          >
            Back to register
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-impact-plus-page pb-10 pt-6">
      <div className="mx-auto max-w-[1180px] px-4 md:px-6">

        <RiskPlusTopNav useCaseChatId={useCaseChatId} secondaryNav={secondaryNav} />

        {/*
        <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-xs md:flex-row md:items-start md:justify-between md:gap-6 md:p-4">
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-2xl font-bold tracking-tight text-deep-blue">{title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {(brief.contentStatus === 'processed' || brief.contentStatus === 'expertValidated') && (
                <span className="inline-flex rounded-[6px] bg-slate-gray px-3 py-1 text-xxs font-semibold leading-none text-deep-blue">
                  Processed
                </span>
              )}
              {brief.contentStatus === 'sufficient' && (
                <span className="inline-flex rounded-[6px] bg-slate-gray px-3 py-1 text-xxs font-semibold leading-none text-royal-blue">
                  Ready for processing
                </span>
              )}
              {(brief.contentStatus === 'none' || brief.contentStatus === 'partial') && (
                <span className="inline-flex rounded-[6px] bg-soft-gray px-3 py-1 text-xxs font-semibold leading-none text-gray-500">
                  Insufficient Content
                </span>
              )}
              {riskReport && (
                <span className="inline-flex items-center gap-2 rounded-[6px] bg-soft-gray px-3 py-1 text-xxs font-semibold text-assured-dark-blue">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ background: riskColor(riskReport.executiveReadinessVerdict.overallRiskRank) }}
                  />
                  {riskLabel(riskReport.executiveReadinessVerdict.overallRiskRank)} Risk
                </span>
              )}
              {riskReport?.titlePage.riskAssessmentStage && (
                <span className="inline-flex rounded-[6px] bg-gray-100 px-3 py-1 text-xxs font-medium text-gray-600">
                  {riskReport.titlePage.riskAssessmentStage}
                </span>
              )}
              {riskReport?.titlePage.reportDate && (
                <span className="text-xxs text-gray-400">
                  {new Date(riskReport.titlePage.reportDate).toLocaleDateString(undefined, {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </span>
              )}
            </div>
          </div>

          {showReportsPanel && (
            <div className="shrink-0 rounded-lg border border-gray-200 bg-impact-plus-page px-4 py-3">
              <div className="text-sm font-semibold text-assured-dark-blue">Reports</div>
              <div className="mt-2">
                {hasRiskReport ? (
                  <button
                    type="button"
                    onClick={() => {
                      void loadRiskPlusReportIntoStore(useCaseChatReports!.riskPlusReport!.reportId);
                      setShowRiskModal(true);
                    }}
                    className="inline-flex items-center gap-2 text-xs font-medium text-royal-blue hover:text-deep-blue hover:underline"
                  >
                    <FileText className="h-4 w-4" strokeWidth={2} />
                    Risk+ Report
                  </button>
                ) : resolvedChat ? (
                  <GenerateReportButton
                    useCaseChat={resolvedChat}
                    reportType="risk"
                    label="Generate Risk+ Report"
                    disabled={brief.jobIsActive}
                    onGenerateReport={refreshReports}
                  />
                ) : null}
              </div>
            </div>
          )}
        </div>
        */}

        {isLoadingReport && !riskReport && (
          <div className="mt-4 animate-pulse space-y-3">
            <div className="h-24 rounded-xl bg-gray-100" />
            <div className="h-32 rounded-xl bg-gray-100" />
            <div className="h-48 rounded-xl bg-gray-100" />
          </div>
        )}

        {!isLoadingReport && reportError && !riskReport && (
          <div
            className="mt-4 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 shadow-xs"
            role="alert"
          >
            <div className="font-semibold">Could not load Risk+ report</div>
            <div className="mt-1">{reportError}</div>
            <button
              type="button"
              onClick={() => void loadRiskReport()}
              className="mt-3 text-sm font-medium text-royal-blue hover:text-deep-blue hover:underline"
            >
              Retry
            </button>
          </div>
        )}

        {!isLoadingReport && !riskReport && !reportError && showReportsPanel && (
          <NoReportEmptyState
            resolvedChat={resolvedChat}
            brief={brief}
            refreshReports={refreshReports}
          />
        )}

        {riskReport && (
          <div className="mt-4 flex flex-col gap-4">
            {children(riskReport)}
          </div>
        )}
      </div>

      <ReportDetailModal isOpen={showRiskModal} onClose={() => setShowRiskModal(false)} />
    </div>
  );
}
